import { Box, Text, Group, Loader, Stack, Drawer, Title, Pagination, ActionIcon } from '@mantine/core';
import React, { useState, useEffect, useMemo } from 'react';
import { FaDownload, FaTimes } from 'react-icons/fa';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

import { apiClient } from '../../lib/api-client';
import { downloadDocument } from '../../lib/api';

// Use the local worker file from the public directory
pdfjs.GlobalWorkerOptions.workerSrc = `${window.location.origin}/pdfjs/pdf.worker.min.mjs`;

interface PDFViewerProps {
  fileUrl: string | null;
  isOpen: boolean;
  onClose: () => void;
  initialPage?: number;
  fileName?: string;
}

const PDFViewer: React.FC<PDFViewerProps> = ({ fileUrl, isOpen, onClose, initialPage = 1, fileName = 'Document' }) => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState<number>(initialPage);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [pdfArrayBuffer, setPdfArrayBuffer] = useState<ArrayBuffer | null>(null);

  // Fetch PDF source if needed
  useEffect(() => {
    if (!fileUrl) {
      return;
    }

    setIsLoading(true);
    setError(null);
    setPdfArrayBuffer(null);

    const fetchPdfBlob = async () => {
      try {
        const blob = await apiClient.getBlob(fileUrl, { requiresAuth: true });
        const arrayBuffer = await blob.arrayBuffer();
        setPdfArrayBuffer(arrayBuffer);
        setIsLoading(false);
      } catch (err) {
        console.error('Failed to fetch PDF blob:', err);
        setError('Failed to load PDF document');
        setIsLoading(false);
      }
    };

    fetchPdfBlob();
  }, [fileUrl]);

  // Set the current page when initialPage prop changes
  useEffect(() => {
    if (initialPage && numPages && initialPage <= numPages) {
      setPageNumber(initialPage);
    }
  }, [initialPage, numPages]);

  // Handle successful document load
  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setIsLoading(false);
    // If initialPage is valid, go to that page
    if (initialPage > 0 && initialPage <= numPages) {
      setPageNumber(initialPage);
    } else {
      setPageNumber(1);
    }
  };

  // Handle loading error
  const onDocumentLoadError = (err: Error) => {
    console.error('Error loading PDF:', err);
    setError('Failed to load PDF document');
    setIsLoading(false);
  };

  const handleDownload = async () => {
    if (!fileUrl) {
      return;
    }

    await downloadDocument(fileUrl, fileName || 'Document.pdf');
  };

  const file = useMemo(() => (pdfArrayBuffer ? { data: pdfArrayBuffer } : null), [pdfArrayBuffer]);

  // Clear PDF data when viewer is closed to avoid detached ArrayBuffer issues
  useEffect(() => {
    if (!isOpen) {
      setPdfArrayBuffer(null);
    }
  }, [isOpen]);

  return (
    <Drawer
      opened={isOpen}
      onClose={onClose}
      position="right"
      size="xl"
      withCloseButton={false}
      styles={{
        body: {
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0,
        },
      }}
    >
      {/* Custom PDF Viewer Header */}
      <Box
        p="md"
        style={{
          borderBottom: '1px solid var(--mantine-color-gray-3)',
        }}
      >
        <Group justify="space-between" align="center" style={{ width: '100%' }}>
          <Title order={4}>{fileName}</Title>
          <Group gap="xs">
            <ActionIcon
              variant="filled"
              color="gray"
              size="lg"
              aria-label="Download PDF"
              onClick={handleDownload}
              disabled={!fileUrl}
            >
              <FaDownload size={18} />
            </ActionIcon>
            <ActionIcon variant="subtle" color="gray" size="lg" aria-label="Close PDF viewer" onClick={onClose}>
              <FaTimes size={18} />
            </ActionIcon>
          </Group>
        </Group>
      </Box>
      {/* PDF Document */}
      <Box
        style={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          display: 'flex',
          justifyContent: 'center',
          backgroundColor: 'var(--mantine-color-gray-1)',
        }}
      >
        {file ? (
          <Document
            file={file}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            loading={
              <Stack align="center" justify="center" style={{ height: '100%', width: '100%', padding: '2rem' }}>
                <Loader size="lg" />
                <Text>Loading PDF...</Text>
              </Stack>
            }
            error={
              <Stack align="center" justify="center" style={{ height: '100%', width: '100%', padding: '2rem' }}>
                <Text c="error" fw={600}>
                  Failed to load PDF
                </Text>
                <Text>{error}</Text>
              </Stack>
            }
          >
            <Box p="xl" style={{ display: 'flex', justifyContent: 'center' }}>
              <Page pageNumber={pageNumber} renderTextLayer={true} renderAnnotationLayer={true} width={600} />
            </Box>
          </Document>
        ) : (
          <Stack align="center" justify="center" style={{ height: '100%', width: '100%', padding: '2rem' }}>
            {isLoading ? (
              <>
                <Loader size="lg" />
                <Text>Loading PDF...</Text>
              </>
            ) : (
              <Text fw={600}>{error || 'No document to display'}</Text>
            )}
          </Stack>
        )}
      </Box>
      <Box
        p="md"
        style={{
          borderTop: '1px solid var(--mantine-color-gray-3)',
        }}
      >
        <Group justify="center" align="center">
          <Pagination
            total={numPages || 1}
            value={pageNumber}
            onChange={setPageNumber}
            disabled={isLoading || !numPages || numPages === 1}
            size="sm"
            radius="md"
          />
        </Group>
      </Box>
    </Drawer>
  );
};

export default PDFViewer;
